import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { profileService } from "../../services/profile.service";
import AccountOrders from "./AccountOrders";
import orderService from "../../services/order.service";
import favouriteService from "../../services/favourite.service"; // 👈 NEW: Imported the service
import { logoutUser } from "../../store/authSlice";
import { authService } from "../../services/auth.service";

// Assuming you have these icons or similar in your project
import UserIcon from "../../assets/icons/UserIcon";
import HeartIcon from "../../assets/icons/HeartIcon";
import OrderIcon from "../../assets/icons/OrderIcon";
import AddressIcon from "../../assets/icons/AddressIcon";
import "./AccountPage.css";

// ⭐️ Static List of Indian States
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

// ===============================================
// 🎯 Pincode Lookup API
// ===============================================
const fetchLocationByPincode = async (pincode) => {
  if (pincode.length !== 6) return { city: "", state: "", status: "pending" };
  try {
    const response = await fetch(
      `https://api.postalpincode.in/pincode/${pincode}`,
    );
    if (!response.ok) throw new Error("API failed");
    const data = await response.json();
    if (data && data[0].Status === "Success" && data[0].PostOffice.length > 0) {
      const postOffice = data[0].PostOffice[0];
      return {
        city: postOffice.District,
        state: postOffice.State,
        status: "success",
      };
    }
    return { city: "", state: "", status: "not_found" };
  } catch (error) {
    return { city: "", state: "", status: "error" };
  }
};

const usePincodeLookup = (pincodeInput) => {
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincodeLookupLoading, setPincodeLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");

  useEffect(() => {
    if (pincodeInput.length !== 6) {
      setCity("");
      setState("");
      setLookupError("");
      return;
    }
    const lookup = async () => {
      setPincodeLookupLoading(true);
      setLookupError("");
      try {
        const location = await fetchLocationByPincode(pincodeInput);
        if (location.status === "success") {
          setCity(location.city);
          setState(location.state);
          setLookupError("");
        } else {
          setCity("");
          setState("");
          setLookupError("Wrong Pincode.");
        }
      } catch (error) {
        setCity("");
        setState("");
        setLookupError("Service unavailable.");
      } finally {
        setPincodeLookupLoading(false);
      }
    };
    const timeoutId = setTimeout(lookup, 500);
    return () => clearTimeout(timeoutId);
  }, [pincodeInput]);

  return { city, state, pincodeLookupLoading, lookupError };
};

// ===============================================
// ======== NESTED COMPONENTS ========
// ===============================================

const AccountProfile = React.forwardRef(({ userData }, ref) => {
  const [formData, setFormData] = useState({
    firstName: userData.firstName || "",
    lastName: userData.lastName || "",
    phone: userData.phone || "",
    email: userData.email || "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.id]: e.target.value });

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      await profileService.updateProfileDetails({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
      });
      setMessage("Profile updated successfully!");
    } catch (err) {
      setMessage("Error: Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-section fade-in">
      <div className="section-header" ref={ref}>
        <h3>Personal Information</h3>
        <p>Update your name, email, and phone number here.</p>
      </div>
      {message && (
        <div
          className={`form-message ${message.startsWith("Error") ? "error" : "success"}`}
        >
          {message}
        </div>
      )}
      <form className="profile-form" onSubmit={handleUpdate}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              disabled
              className="disabled-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>
        </div>
        <button type="submit" className="btn-primary mt-4" disabled={loading}>
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </form>
    </div>
  );
});

const NewAddressForm = ({ onSave, onCancel }) => {
  const [addressData, setAddressData] = useState({
    name: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [loading, setLoading] = useState(false);
  const {
    city: autoCity,
    state: autoState,
    pincodeLookupLoading,
    lookupError,
  } = usePincodeLookup(addressData.pincode);

  useEffect(() => {
    if (autoCity || autoState)
      setAddressData((prev) => ({ ...prev, city: autoCity, state: autoState }));
  }, [autoCity, autoState]);

  const handleChange = (e) =>
    setAddressData({ ...addressData, [e.target.id]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lookupError) return alert("Check Pincode error.");
    setLoading(true);
    try {
      const newAddress = await profileService.saveAddress(addressData);
      onSave(newAddress);
    } catch (err) {
      alert("Failed to save address.");
    } finally {
      setLoading(false);
    }
  };

  const isCityStateDisabled =
    !!autoCity ||
    !!lookupError ||
    pincodeLookupLoading ||
    addressData.pincode.length < 6;

  return (
    <form className="profile-form inline-form fade-in" onSubmit={handleSubmit}>
      <h4>Add New Address</h4>
      <div className="form-group">
        <label htmlFor="name">Address Nickname</label>
        <input
          type="text"
          id="name"
          value={addressData.name}
          onChange={handleChange}
          required
          placeholder="E.g., Home"
        />
      </div>
      <div className="form-group">
        <label htmlFor="addressLine1">Address Line 1</label>
        <input
          type="text"
          id="addressLine1"
          value={addressData.addressLine1}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="addressLine2">Address Line 2 (Optional)</label>
        <input
          type="text"
          id="addressLine2"
          value={addressData.addressLine2}
          onChange={handleChange}
        />
      </div>
      <div className="form-row three-cols">
        <div className="form-group">
          <label htmlFor="pincode">Pincode</label>
          <input
            type="text"
            id="pincode"
            value={addressData.pincode}
            onChange={handleChange}
            maxLength="6"
            required
          />
          {pincodeLookupLoading && (
            <small className="helper-text info">Fetching...</small>
          )}
          {lookupError && (
            <small className="helper-text error">{lookupError}</small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="state">State</label>
          <input
            type="text"
            id="state"
            list="indian-states"
            value={addressData.state}
            onChange={handleChange}
            required
            disabled={isCityStateDisabled}
          />
          <datalist id="indian-states">
            {INDIAN_STATES.map((state) => (
              <option key={state} value={state} />
            ))}
          </datalist>
        </div>
        <div className="form-group">
          <label htmlFor="city">City</label>
          <input
            type="text"
            id="city"
            value={addressData.city}
            onChange={handleChange}
            required
            disabled={isCityStateDisabled}
          />
        </div>
      </div>
      <div className="form-actions">
        <button
          type="submit"
          className="btn-primary"
          disabled={loading || !!lookupError || !autoCity}
        >
          {loading ? "Saving..." : "Save Address"}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
      </div>
    </form>
  );
};

const EditAddressForm = ({ address, onUpdate, onCancel }) => {
  const [formData, setFormData] = useState({
    ...address,
    isDefault: address.isDefault || false,
  });
  const [loading, setLoading] = useState(false);
  const {
    city: autoCity,
    state: autoState,
    pincodeLookupLoading,
    lookupError,
  } = usePincodeLookup(formData.pincode);

  useEffect(() => {
    if (autoCity || autoState)
      setFormData((prev) => ({ ...prev, city: autoCity, state: autoState }));
  }, [autoCity, autoState]);

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData({ ...formData, [id]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lookupError) return alert("Check Pincode error.");
    setLoading(true);
    try {
      await profileService.updateAddress(address.id, formData);
      onUpdate(address.id, formData);
    } catch (err) {
      alert("Failed to update.");
    } finally {
      setLoading(false);
    }
  };

  const isCityStateDisabled =
    !!autoCity ||
    !!lookupError ||
    pincodeLookupLoading ||
    formData.pincode.length < 6;

  return (
    <form className="profile-form inline-form fade-in" onSubmit={handleSubmit}>
      <h4>Edit Address</h4>
      <div className="form-group">
        <label htmlFor="fullName">Address Nickname</label>
        <input
          type="text"
          id="fullName"
          value={formData.fullName}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="addressLine1">Address Line 1</label>
        <input
          type="text"
          id="addressLine1"
          value={formData.addressLine1}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor="addressLine2">Address Line 2 (Optional)</label>
        <input
          type="text"
          id="addressLine2"
          value={formData.addressLine2}
          onChange={handleChange}
        />
      </div>
      <div className="form-row three-cols">
        <div className="form-group">
          <label htmlFor="pincode">Pincode</label>
          <input
            type="text"
            id="pincode"
            value={formData.pincode}
            onChange={handleChange}
            maxLength="6"
            required
          />
          {lookupError && (
            <small className="helper-text error">{lookupError}</small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="state">State</label>
          <input
            type="text"
            id="state"
            list="indian-states"
            value={formData.state}
            onChange={handleChange}
            required
            disabled={isCityStateDisabled}
          />
        </div>
        <div className="form-group">
          <label htmlFor="city">City</label>
          <input
            type="text"
            id="city"
            value={formData.city}
            onChange={handleChange}
            required
            disabled={isCityStateDisabled}
          />
        </div>
      </div>
      <div className="form-group checkbox-group">
        <label>
          <input
            type="checkbox"
            id="isDefault"
            checked={formData.isDefault}
            onChange={handleChange}
          />
          Set as Default Address
        </label>
      </div>
      <div className="form-actions">
        <button
          type="submit"
          className="btn-primary"
          disabled={loading || !!lookupError || !autoCity}
        >
          {loading ? "Saving..." : "Update Address"}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
      </div>
    </form>
  );
};

const AccountAddresses = React.forwardRef((props, ref) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    const loadAddresses = async () => {
      try {
        const data = await profileService.fetchAddresses();
        setAddresses(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadAddresses();
  }, []);

  const handleNewAddressSave = (newAddress) => {
    setAddresses((prev) => [...prev, newAddress]);
    setShowNewForm(false);
  };
  const handleUpdate = (id, updatedData) => {
    setAddresses((prev) =>
      prev.map((addr) => (addr.id === id ? { ...addr, ...updatedData } : addr)),
    );
    setEditingId(null);
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await profileService.deleteAddress(id);
      setAddresses((prev) => prev.filter((addr) => addr.id !== id));
    } catch (err) {
      alert("Failed to delete.");
    }
  };

  if (loading) return <div className="account-section skeleton-loader"></div>;

  return (
    <div className="account-section fade-in">
      <div className="section-header split-header" ref={ref}>
        <h3>Saved Addresses</h3>
        {!showNewForm && (
          <button
            className="btn-primary btn-sm"
            onClick={() => {
              setShowNewForm(true);
              setEditingId(null);
            }}
          >
            + Add New Address
          </button>
        )}
      </div>

      {showNewForm && (
        <NewAddressForm
          onSave={handleNewAddressSave}
          onCancel={() => setShowNewForm(false)}
        />
      )}

      <div className="address-list">
        {addresses.length === 0 && !showNewForm ? (
          <div className="empty-state">
            <i className="fas fa-map-marked-alt empty-icon"></i>
            <p>No saved addresses.</p>
          </div>
        ) : (
          addresses.map((addr) => (
            <React.Fragment key={addr.id}>
              {editingId === addr.id ? (
                <EditAddressForm
                  address={addr}
                  onUpdate={handleUpdate}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <div
                  className={`address-card ${addr.isDefault ? "default-address" : ""}`}
                >
                  <div className="address-card-header">
                    <h4>
                      {addr.fullName}{" "}
                      {addr.isDefault && (
                        <span className="badge-success">Default</span>
                      )}
                    </h4>
                  </div>
                  <div className="address-card-body">
                    <p>{addr.addressLine1}</p>
                    {addr.addressLine2 && <p>{addr.addressLine2}</p>}
                    <p>
                      {addr.city}, {addr.state} -{" "}
                      <strong>{addr.pincode}</strong>
                    </p>
                  </div>
                  <div className="address-actions">
                    <button
                      className="action-btn edit"
                      onClick={() => setEditingId(addr.id)}
                    >
                      Edit
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(addr.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );
});

const AccountDashboard = React.forwardRef(
  ({ userData, onNavigate, onTabSwitch }, ref) => {
    const { user } = useSelector((state) => state.auth);
    const navigate = useNavigate();

    const [dashboardData, setDashboardData] = useState({
      orderCount: 0,
      addressCount: 0,
      wishlistCount: 0,
      latestOrder: null,
      loading: true,
    });

    useEffect(() => {
      const fetchDashboardStats = async () => {
        try {
          const addresses = await profileService.fetchAddresses();

          let orders = [];
          try {
            const orderResponse = await orderService.getMyOrders();
            // ✅ SAFELY parse orders whether it's an array or wrapped in an object
            if (Array.isArray(orderResponse)) {
              orders = orderResponse;
            } else if (orderResponse && Array.isArray(orderResponse.data)) {
              orders = orderResponse.data;
            }
          } catch (err) {
            console.error("Could not fetch orders:", err);
          }

          let wishlist = [];
          try {
            const favs = await favouriteService.getFavourites();
            if (Array.isArray(favs)) wishlist = favs;
          } catch (err) {
            console.error("Could not fetch wishlist:", err);
          }

          setDashboardData({
            addressCount: addresses?.length || 0,
            orderCount: orders?.length || 0,
            wishlistCount: wishlist.length || 0,
            latestOrder: orders?.length > 0 ? orders[0] : null,
            loading: false,
          });
        } catch (error) {
          console.error("Failed to load dashboard data:", error);
          setDashboardData((prev) => ({ ...prev, loading: false }));
        }
      };

      fetchDashboardStats();
    }, []);

    const formatDate = (dateString) => {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    if (dashboardData.loading) {
      return <div className="account-section skeleton-loader" ref={ref}></div>;
    }

    // ✅ SAFELY EXTRACT PRODUCT NAME AND IMAGE
    const latestOrderItem = dashboardData.latestOrder?.OrderItems?.[0];
    const recentProductName = latestOrderItem?.Inventory?.name || latestOrderItem?.product_name || latestOrderItem?.name || "Zynvert Battery Pack";
    const recentProductImage = latestOrderItem?.Inventory?.image_url || latestOrderItem?.image_url;

    return (
      <div className="account-section fade-in" ref={ref}>
        <div className="welcome-banner desktop-welcome-banner">
          <h3>
            Welcome back,{" "}
            <span>
              {userData.firstName || user?.email?.split("@")[0] || "User"}
            </span>
            !
          </h3>
          <p>Here is a quick overview of your Zynvert account.</p>
        </div>

        <div className="dashboard-promo-banner">
          <div className="promo-content">
            <h4>Power up your ride! ⚡</h4>
            <p>Get 10% off your next high-capacity BMS or battery purchase.</p>
          </div>
          <button
            className="btn-primary btn-sm"
            onClick={() => navigate("/shop")}
          >
            Shop Now
          </button>
        </div>

        <div className="dashboard-grid mt-4">
          <div
            className="summary-card interactive"
            onClick={() => onTabSwitch("orders")}
          >
            <div className="summary-icon green-icon">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg>
            </div>
            <div className="summary-info">
              <h4>Total Purchases</h4>
              <p>
                {dashboardData.orderCount === 0
                  ? "No history yet"
                  : `${dashboardData.orderCount} item${dashboardData.orderCount > 1 ? "s" : ""}`}
              </p>
            </div>
            <div className="summary-arrow">&rarr;</div>
          </div>

          <div
            className="summary-card interactive"
            onClick={() => onNavigate("favourite")}
          >
            <div className="summary-icon green-icon">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </div>
            <div className="summary-info">
              <h4>Wishlist</h4>
              <p>
                {dashboardData.wishlistCount} Saved item
                {dashboardData.wishlistCount !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="summary-arrow">&rarr;</div>
          </div>

          <div
            className="summary-card interactive"
            onClick={() => onTabSwitch("addresses")}
          >
            <div className="summary-icon green-icon">
              <svg
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </div>
            <div className="summary-info">
              <h4>Addresses</h4>
              <p>
                {dashboardData.addressCount} Saved location
                {dashboardData.addressCount !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="summary-arrow">&rarr;</div>
          </div>
        </div>

        <div className="recent-order-snapshot mt-4">
          <div className="snapshot-header">
            <h4>Recent Purchase</h4>
          </div>

          {dashboardData.latestOrder ? (
            <div className="snapshot-card">
              <div className="snapshot-top">
                <span className="order-id">
                  #{dashboardData.latestOrder.order_id}
                </span>
                <span className="order-date">
                  {formatDate(dashboardData.latestOrder.createdAt)}
                </span>
              </div>
              <div className="snapshot-middle">
                <div className="snapshot-product">
                  <div className="product-placeholder-img">
                    {recentProductImage ? (
                      <img src={recentProductImage} alt={recentProductName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} />
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        fill="none"
                        stroke="#94a3b8"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                      </svg>
                    )}
                  </div>
                  <div className="product-details">
                    <strong>
                      {recentProductName}
                    </strong>
                    <span className="product-price">
                      ₹{dashboardData.latestOrder.total_amount}
                    </span>
                  </div>
                </div>
              </div>
              <div className="snapshot-bottom">
                <span
                  className={`status-badge ${(dashboardData.latestOrder.status || "processing").toLowerCase().replace(" ", "-")}`}
                >
                  <span className="pulse-dot"></span>{" "}
                  {dashboardData.latestOrder.status || "Processing"}
                </span>
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => onNavigate("orders")}
                >
                  Track
                </button>
              </div>
            </div>
          ) : (
            <div
              className="snapshot-card"
              style={{ textAlign: "center", padding: "40px 20px" }}
            >
              <p
                style={{ color: "var(--acc-text-muted)", marginBottom: "16px" }}
              >
                You haven't purchased anything yet.
              </p>
              <button
                className="btn-primary btn-sm"
                onClick={() => navigate("/shop")}
              >
                Browse Products
              </button>
            </div>
          )}
        </div>
      </div>
    );
  },
);

// ===============================================
// ======== MAIN ACCOUNT PAGE COMPONENT ========
// ===============================================

const AccountPage = ({ onNavigate }) => {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(false);

  // Initialize Refs
  const profileRef = useRef(null);
  const ordersRef = useRef(null);
  const addressesRef = useRef(null);
  const dashboardRef = useRef(null);

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("tab") || "dashboard";
  });

  const [profileData, setProfileData] = useState({
    firstName: user?.displayName?.split(" ")[0] || "",
    lastName: user?.displayName?.split(" ").slice(1).join(" ") || "",
    phone: "",
    email: user?.email || "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get("tab");
    if (tabFromUrl) setActiveTab(tabFromUrl);
  }, [location.search]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const backendData = await profileService.fetchProfileDetails();
        setProfileData((prev) => ({ ...prev, ...backendData }));
      } catch (err) {
        console.warn("Could not fetch backend profile");
      } finally {
        setLoading(false);
      }
    };
    if (user) loadProfile();
  }, [user]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    setTimeout(() => {
      let currentRef = null;
      switch (tab) {
        case "profile":
          currentRef = profileRef;
          break;
        case "orders":
          currentRef = ordersRef;
          break;
        case "addresses":
          currentRef = addressesRef;
          break;
        case "dashboard":
          currentRef = dashboardRef;
          break;
        default:
          break;
      }

      if (currentRef && currentRef.current) {
        const yOffset = -80;
        const element = currentRef.current;
        const y =
          element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 100);
  };

  const handleLogout = async () => {
    const confirmLogout = window.confirm("Are you sure you want to logout?");

    if (confirmLogout) {
      try {
        await authService.logout();
        dispatch(logoutUser());
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      } catch (error) {
        console.error("Logout failed:", error);
        alert("There was an error logging out. Please try again.");
      }
    }
  };

  const renderFooterLinks = () => (
    <>
      <div className="legal-links">
        <Link to="/about-us">About Us</Link>
        <Link to="/privacy-policy">Privacy Policy</Link>
        <Link to="/refund-cancellations">Refund & Cancellations</Link>
        <Link to="/terms-conditions">Terms & Conditions</Link>
      </div>

      <button className="logout-btn" onClick={handleLogout}>
        <i className="fas fa-sign-out-alt"></i> Logout
      </button>

      <div className="app-version">Zynvert Web v1.0.0</div>
    </>
  );

  const renderContent = () => {
    if (loading) return <div className="account-section skeleton-loader"></div>;

    switch (activeTab) {
      case "dashboard":
        return (
          <AccountDashboard
            ref={dashboardRef}
            userData={profileData}
            onNavigate={onNavigate} // Pass onNavigate here directly
            onTabSwitch={handleTabChange}
          />
        );
      case "profile":
        return <AccountProfile ref={profileRef} userData={profileData} />;
      case "orders":
        return (
          <div ref={ordersRef}>
            <AccountOrders />
          </div>
        );
      case "addresses":
        return <AccountAddresses ref={addressesRef} />;
      default:
        return (
          <AccountDashboard
            ref={dashboardRef}
            userData={profileData}
            onNavigate={onNavigate}
            onTabSwitch={handleTabChange}
          />
        );
    }
  };

  return (
    <div className={`account-page ${isVisible ? "visible" : ""}`}>
      <div className="account-container">
        <h1 className="page-title">My Account</h1>

        <div className="account-layout">
          <aside className="account-sidebar">
            <div className="mobile-welcome-banner">
              <h2>
                Welcome back, <span>{profileData.firstName || "User"}</span>!
              </h2>
              <p>
                Manage your Zynvert orders, profile, and preferences all from
                one place.
              </p>
            </div>

            <nav className="sidebar-nav">
              <button
                className={`desktop-only-btn ${activeTab === "dashboard" ? "active" : ""}`}
                onClick={() => handleTabChange("dashboard")}
              >
                <i className="fas fa-home"></i> Dashboard
              </button>
              <button
                className={activeTab === "profile" ? "active" : ""}
                onClick={() => handleTabChange("profile")}
              >
                <div
                  className="icon-wrapper"
                  style={{ color: "var(--acc-primary)" }}
                >
                  <UserIcon />
                </div>{" "}
                Profile
              </button>
              <button
                className={activeTab === "orders" ? "active" : ""}
                onClick={() => handleTabChange("orders")}
              >
                <div
                  className="icon-wrapper"
                  style={{ color: "var(--acc-primary)" }}
                >
                  <OrderIcon />
                </div>{" "}
                Orders
              </button>

              {/* 👈 NEW: Directly navigates to favourite page instead of switching tabs */}
              <button onClick={() => onNavigate("favourite")}>
                <div
                  className="icon-wrapper"
                  style={{ color: "var(--acc-primary)" }}
                >
                  <HeartIcon />
                </div>{" "}
                Wishlist
              </button>

              <button
                className={activeTab === "addresses" ? "active" : ""}
                onClick={() => handleTabChange("addresses")}
              >
                <div
                  className="icon-wrapper"
                  style={{ color: "var(--acc-primary)" }}
                >
                  <AddressIcon />
                </div>{" "}
                Addresses
              </button>
            </nav>

            <div className="sidebar-footer desktop-footer">
              {renderFooterLinks()}
            </div>
          </aside>

          <section className="account-content">{renderContent()}</section>

          <div className="sidebar-footer mobile-footer">
            {renderFooterLinks()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;